import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Grid,
  Paper,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, Search, Article } from '@mui/icons-material';
import { WhatsappTemplate } from '../../services/whatsappService';
import { SPOP } from '../../types';
import {
  TEMPLATE_TYPE_FILTER_OPTIONS,
  TEMPLATE_TYPE_OPTIONS,
  getTemplateTypeLabel,
} from '../../constants/whatsappTemplateConstants';

interface Props {
  templates: WhatsappTemplate[];
  spops: SPOP[];
  operatorSpopId?: string;
  onAdd: () => void;
  onEdit: (template: WhatsappTemplate) => void;
  onDelete: (id: number) => void;
}

const GROUP_ORDER = ['reminder', 'tagihan', 'pembayaran', 'lainnya'] as const;
const GROUP_LABELS: Record<string, string> = {
  reminder: 'Reminder otomatis',
  tagihan: 'Tagihan',
  pembayaran: 'Pembayaran',
  lainnya: 'Lainnya',
};

const WhatsappTemplatesPanel: React.FC<Props> = ({
  templates,
  spops,
  operatorSpopId,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const canModify = (template: WhatsappTemplate) =>
    !operatorSpopId || template.spop_id === operatorSpopId;
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (typeFilter && t.type !== typeFilter) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.template.toLowerCase().includes(q) ||
        getTemplateTypeLabel(t.type).toLowerCase().includes(q)
      );
    });
  }, [templates, search, typeFilter]);

  const grouped = useMemo(() => {
    const map = new Map<string, WhatsappTemplate[]>();
    filtered.forEach((t) => {
      const group =
        TEMPLATE_TYPE_OPTIONS.find((o) => o.value === t.type)?.group || 'lainnya';
      const list = map.get(group) || [];
      list.push(t);
      map.set(group, list);
    });
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({ group: g, items: map.get(g)! }));
  }, [filtered]);

  const getSpopName = (spopId?: string | null) =>
    spops.find((s) => s.id === spopId)?.name || spopId;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Template Pesan
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {templates.length} template · {filtered.length} ditampilkan
              {operatorSpopId ? ' · template default hanya baca' : ''}
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={onAdd}>
            Tambah template
          </Button>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            size="small"
            placeholder="Cari nama atau isi pesan..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
            }}
          />
          <TextField
            select
            size="small"
            label="Filter tipe"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: { sm: 220 } }}
          >
            {TEMPLATE_TYPE_FILTER_OPTIONS.map((o) => (
              <MenuItem key={o.value || 'all'} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {filtered.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Article sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {templates.length === 0
                ? 'Belum ada template. Tambahkan template untuk reminder dan notifikasi.'
                : 'Tidak ada template yang cocok dengan filter.'}
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={3}>
            {grouped.map(({ group, items }) => (
              <Box key={group}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 700 }}>
                  {GROUP_LABELS[group] || group}
                </Typography>
                <Grid container spacing={2}>
                  {items.map((template) => (
                    <Grid key={template.id} size={{ xs: 12, md: 6 }}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          borderColor: template.isActive === false ? 'divider' : 'primary.light',
                          opacity: template.isActive === false ? 0.75 : 1,
                        }}
                      >
                        <CardContent sx={{ pb: '16px !important' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle1" fontWeight={700} noWrap>
                                {template.name}
                              </Typography>
                              <Chip
                                label={getTemplateTypeLabel(template.type)}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                            {canModify(template) ? (
                              <Stack direction="row" spacing={0.5}>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => onEdit(template)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Hapus">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => template.id && onDelete(template.id)}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            ) : (
                              <Chip label="Default" size="small" variant="outlined" color="info" />
                            )}
                          </Stack>

                          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                            {template.status ? (
                              <Chip label={`Status: ${template.status}`} size="small" variant="outlined" color="primary" />
                            ) : null}
                            {template.spop_id ? (
                              <Chip
                                label={`SPOP: ${getSpopName(template.spop_id)}`}
                                size="small"
                                variant="outlined"
                                color="secondary"
                              />
                            ) : null}
                            {!template.status && !template.spop_id && (
                              <Chip label="Default" size="small" variant="outlined" color="info" />
                            )}
                            <Chip
                              label={template.isActive !== false ? 'Aktif' : 'Nonaktif'}
                              size="small"
                              color={template.isActive !== false ? 'success' : 'default'}
                            />
                          </Stack>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 1.5,
                              whiteSpace: 'pre-wrap',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {template.template}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default WhatsappTemplatesPanel;
